<template>
  <div class="ql-editor-ave max-w-full">
    <!-- Upload Diagnostic Panel (always visible during/after upload) -->
    <div v-if="isUploading || showDiagnostic" class="diagnostic-panel">
      <div class="diagnostic-header">
        <span class="diagnostic-title">{{ isUploading ? 'Загрузка файла' : (uploadFailed ? 'Ошибка загрузки' : 'Загрузка завершена') }}</span>
        <div class="diagnostic-header-actions">
          <button class="diagnostic-copy-btn-sm" @click="copyDiagnostic">Копировать лог</button>
          <button v-if="!isUploading" class="diagnostic-close" @click="showDiagnostic = false">&times;</button>
        </div>
      </div>
      <div v-if="isUploading" class="diagnostic-progress">
        <div class="upload-progress-container">
          <div class="upload-progress-bar" :style="{ width: uploadProgress + '%' }"></div>
        </div>
        <span class="upload-pct">{{ uploadProgress }}%</span>
      </div>
      <pre class="diagnostic-log" ref="diagnosticLogEl">{{ diagnosticLog.join('\n') }}</pre>
    </div>
    
    <QuillEditor
      ref="quillEditor"
      v-model:content="localContent"
      @update:content="onUpdate"
      contentType="html"
      :options="quillOptions"
      :style="editorStyle"
    />
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, onMounted } from 'vue'
import { QuillEditor, Quill } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { usePortalStore } from "@/stores/portalStore";
import api from '@/services/api'
import { useToast } from 'vue-toastification'

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov', '.m4v']

const TOOLBAR_PICKER_TITLES = {
  'ql-header': 'Заголовок',
  'ql-size': 'Размер текста',
  'ql-color': 'Цвет текста',
  'ql-background': 'Цвет фона',
  'ql-align': 'Выравнивание',
}

const TOOLBAR_BUTTON_TITLES = {
  'ql-bold': 'Полужирный',
  'ql-italic': 'Курсив',
  'ql-underline': 'Подчеркнутый',
  'ql-strike': 'Зачеркнутый',
  'ql-blockquote': 'Цитата',
  'ql-code-block': 'Блок кода',
  'ql-link': 'Ссылка',
  'ql-image': 'Загрузить изображение',
  'ql-video': 'Загрузить видео',
  'ql-clean': 'Очистить форматирование',
}

// Кнопки, для которых используем нативный title (из-за конфликта с иконками)
const NATIVE_TITLE_BUTTONS = {
  'ql-file': 'Загрузить файл',
}

const applyToolbarTooltips = (toolbarContainer) => {
  console.log('[Quill Tooltips] applyToolbarTooltips called, container:', toolbarContainer)
  if (!toolbarContainer) {
    console.warn('[Quill Tooltips] No toolbar container!')
    return
  }

  const buttons = toolbarContainer.querySelectorAll('button')
  const pickers = toolbarContainer.querySelectorAll('.ql-picker')
  console.log('[Quill Tooltips] Found buttons:', buttons.length, 'pickers:', pickers.length)

  const setTooltip = (el, text) => {
    if (!el || !text) return
    if (el.hasAttribute('data-tooltip')) return
    el.setAttribute('data-tooltip', text)
    el.classList.add('ql-tooltip-target')
    console.log('[Quill Tooltips] Set tooltip:', text, 'on', el.className)
  }

  const setNativeTitle = (el, text) => {
    if (!el || !text) return
    if (el.hasAttribute('title')) return
    el.setAttribute('title', text)
  }

  toolbarContainer.querySelectorAll('button').forEach((btn) => {
    // Кнопки с нативным title (из-за конфликта с иконками)
    const nativeKey = Object.keys(NATIVE_TITLE_BUTTONS).find((k) => btn.classList.contains(k))
    if (nativeKey) {
      setNativeTitle(btn, NATIVE_TITLE_BUTTONS[nativeKey])
      return
    }

    if (btn.classList.contains('ql-list')) {
      const v = String(btn.value || '').trim()
      if (v === 'ordered') setTooltip(btn, 'Нумерованный список')
      else if (v === 'bullet') setTooltip(btn, 'Маркированный список')
      return
    }
    if (btn.classList.contains('ql-indent')) {
      const v = String(btn.value || '').trim()
      if (v === '-1') setTooltip(btn, 'Уменьшить отступ')
      else if (v === '+1') setTooltip(btn, 'Увеличить отступ')
      return
    }

    const key = Object.keys(TOOLBAR_BUTTON_TITLES).find((k) => btn.classList.contains(k))
    if (key) {
      setTooltip(btn, TOOLBAR_BUTTON_TITLES[key])
    } else {
      console.warn('[Quill Tooltips] No tooltip for button:', btn.className)
    }
  })

  // Для picker'ов используем нативный title (::before занят текстом значения)
  toolbarContainer.querySelectorAll('.ql-picker').forEach((picker) => {
    const key = Object.keys(TOOLBAR_PICKER_TITLES).find((k) => picker.classList.contains(k))
    if (!key) return
    const label = picker.querySelector('.ql-picker-label')
    setNativeTitle(label || picker, TOOLBAR_PICKER_TITLES[key])
  })
}

// Создаем кастомные блоты для файлов и видео
const BlockEmbed = Quill.import('blots/block/embed');

class FileBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute('data-file-url', value.url);
    node.innerHTML = `
      <div class="file-block">
        <div class="file-icon">
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14.4034 3.52843L11.2019 0.499574C10.8614 0.177404 10.4157 0 9.94689 0H3.85156C2.84461 0 2.02539 0.819221 2.02539 1.82617V15.1738C2.02539 16.1808 2.84461 17 3.85156 17H13.1484C14.1554 17 14.9746 16.1808 14.9746 15.1738V4.85503C14.9746 4.35535 14.7664 3.87182 14.4034 3.52843ZM13.436 3.98438H10.957C10.8655 3.98438 10.791 3.9099 10.791 3.81836V1.48205L13.436 3.98438ZM13.1484 16.0039H3.85156C3.39386 16.0039 3.02148 15.6315 3.02148 15.1738V1.82617C3.02148 1.36847 3.39386 0.996094 3.85156 0.996094H9.79492V3.81836C9.79492 4.45915 10.3162 4.98047 10.957 4.98047H13.9785V15.1738C13.9785 15.6315 13.6061 16.0039 13.1484 16.0039Z" fill="#565656"/>
          <path d="M12.0527 6.64062H4.74805C4.47299 6.64062 4.25 6.86362 4.25 7.13867C4.25 7.41373 4.47299 7.63672 4.74805 7.63672H12.0527C12.3278 7.63672 12.5508 7.41373 12.5508 7.13867C12.5508 6.86362 12.3278 6.64062 12.0527 6.64062ZM12.0527 9.29688H4.74805C4.47299 9.29688 4.25 9.51987 4.25 9.79492C4.25 10.07 4.47299 10.293 4.74805 10.293H12.0527C12.3278 10.293 12.5508 10.07 12.5508 9.79492C12.5508 9.51987 12.3278 9.29688 12.0527 9.29688ZM7.16258 11.9531H4.74805C4.47299 11.9531 4.25 12.1761 4.25 12.4512C4.25 12.7262 4.47299 12.9492 4.74805 12.9492H7.16258C7.43763 12.9492 7.66062 12.7262 7.66062 12.4512C7.66062 12.1761 7.43763 11.9531 7.16258 11.9531Z" fill="#565656"/>
        </svg>
        </div>
        <div class="file-info">
          <span class="file-name">${value.name}</span>
          <button class="file-download-btn" onclick="window.open('${value.url}', '_blank')">Скачать</button>
        </div>
      </div>
    `;
    return node;
  }

  static value(node) {
    return {
      url: node.getAttribute('data-file-url'),
      name: node.querySelector('.file-name')?.textContent || 'Файл',
    };
  }
}
FileBlot.blotName = 'file-block';
FileBlot.tagName = 'div';
FileBlot.className = 'ql-file-block';

class VideoBlot extends BlockEmbed {
  static blotName = 'video';
  static tagName = 'video';
  static className = 'ql-video';

  static create(value) {
    const node = super.create();
    node.setAttribute('src', value);
    node.setAttribute('controls', '');
    node.setAttribute('playsinline', '');
    node.setAttribute('preload', 'metadata');
    node.style.width = '100%';
    node.style.display = 'block';
    return node;
  }

  static value(node) {
    return node.getAttribute('src') || '';
  }
}

Quill.register(VideoBlot, true);

const portalStore = usePortalStore()
const toast = useToast()

const MAX_FILE_SIZE_MB = 1024
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  height: {
    type: String,
    default: '600px'
  },
  placeholder: {
    type: String,
    default: 'Введите текст...'
  }
})

const emit = defineEmits(['update:modelValue', 'update:content'])

const normalizeVideoEmbeds = (html) => {
  if (!html) {
    return ''
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return html
  }

  const container = document.createElement('div')
  container.innerHTML = html

  const iframes = container.querySelectorAll('iframe.ql-video')

  if (!iframes.length) {
    return html
  }

  iframes.forEach((iframe) => {
    const src = iframe.getAttribute('src') || ''
    const lowerSrc = src.toLowerCase()
    const hasVideoExtension = VIDEO_EXTENSIONS.some((ext) => {
      return lowerSrc.includes(ext)
    })

    if (!src || !hasVideoExtension) {
      return
    }

    const video = document.createElement('video')
    video.className = iframe.className || 'ql-video'
    video.setAttribute('controls', '')
    video.setAttribute('playsinline', '')
    video.setAttribute('preload', 'metadata')
    video.style.width = '100%'
    video.style.display = 'block'
    video.src = src

    if (iframe.parentNode) {
      iframe.parentNode.replaceChild(video, iframe)
    }
  })

  return container.innerHTML
}

const localContent = ref(normalizeVideoEmbeds(props.modelValue || ''))
const quillEditor = ref(null)
const editorStyle = ref(`height: ${props.height}; max-width: 100%;`)
const isUploading = ref(false)
const uploadProgress = ref(0)
const diagnosticLog = ref([])
const showDiagnostic = ref(false)
const uploadFailed = ref(false)
const diagnosticLogEl = ref(null)

const addLog = (msg) => {
  const ts = new Date().toISOString().slice(11, 23)
  diagnosticLog.value.push(`[${ts}] ${msg}`)
  // Автоскролл лога вниз
  setTimeout(() => {
    if (diagnosticLogEl.value) {
      diagnosticLogEl.value.scrollTop = diagnosticLogEl.value.scrollHeight
    }
  }, 0)
}

const copyDiagnostic = () => {
  const text = diagnosticLog.value.join('\n')
  navigator.clipboard.writeText(text).then(() => {
    toast.success('Лог скопирован в буфер обмена')
  }).catch(() => {
    // fallback
    const ta = document.createElement('textarea')
    ta.value = text
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
    toast.success('Лог скопирован в буфер обмена')
  })
}

// Получаем Bitrix auth данные
const getBitrixAuth = () => {
  try {
    const w = window
    if (w.BX24 && typeof w.BX24.getAuth === 'function') {
      return w.BX24.getAuth()
    }
  } catch (e) { void e }
  return null
}

// Загрузка файла через Bitrix Disk REST API (first-party, без throttling)
const uploadViaBitrix = async (file, addLog, onProgress) => {
  const auth = getBitrixAuth()
  if (!auth || !auth.access_token) {
    throw new Error('BX24 auth недоступен')
  }

  const domain = auth.domain || ''
  const token = auth.access_token
  const restBase = `https://${domain}/rest`

  addLog(`Bitrix REST: domain=${domain}`)

  // 1. Получаем хранилище приложения
  addLog(`Получаю хранилище приложения...`)
  const storageRes = await fetch(`${restBase}/disk.storage.getforapp.json?auth=${encodeURIComponent(token)}`)
  const storageData = await storageRes.json()
  const storageId = storageData?.result?.ID || storageData?.result?.id
  if (!storageId) {
    addLog(`Ответ disk.storage.getforapp: ${JSON.stringify(storageData)}`)
    throw new Error('Не удалось получить хранилище Bitrix')
  }
  addLog(`Хранилище ID: ${storageId}`)

  // 2. Получаем root folder ID
  let rootFolderId = ''
  try {
    const infoRes = await fetch(`${restBase}/disk.storage.get.json?auth=${encodeURIComponent(token)}&id=${storageId}`)
    const infoData = await infoRes.json()
    rootFolderId = String(infoData?.result?.ROOT_OBJECT_ID || '')
  } catch (e) { void e }
  addLog(`Root folder ID: ${rootFolderId || 'не найден, загрузка в storage'}`)

  // 3. Загрузка файла через XHR (с прогрессом)
  const uploadUrl = rootFolderId
    ? `${restBase}/disk.folder.uploadfile.json?auth=${encodeURIComponent(token)}&id=${rootFolderId}`
    : `${restBase}/disk.storage.uploadfile.json?auth=${encodeURIComponent(token)}&id=${storageId}`

  addLog(`Загрузка в Bitrix Disk...`)

  const fd = new FormData()
  fd.append('data', JSON.stringify({ NAME: file.name }))
  // При коллизии имени Bitrix сам добавит суффикс («файл (1).jpg»), иначе — ошибка
  // DISK_OBJ_22000 «файл с таким именем уже есть». Проверено на проде: значение 'Y'.
  fd.append('generateUniqueName', 'Y')
  fd.append('fileContent', file, file.name)

  const firstResult = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', uploadUrl, true)

    xhr.upload.addEventListener('progress', (e) => {
      if (onProgress) onProgress(e)
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText)
          const fileData = result?.result
          addLog(`Bitrix ответ: ${JSON.stringify(fileData).slice(0, 500)}`)
          if (!fileData) {
            reject(new Error('Bitrix не вернул данные файла: ' + xhr.responseText))
            return
          }

          // Bitrix может вернуть uploadUrl для двухшагового upload
          if (fileData.uploadUrl && fileData.field) {
            // Нужно отправить файл по uploadUrl
            resolve({ uploadUrl: fileData.uploadUrl, field: fileData.field, name: file.name, needsSecondStep: true })
            return
          }

          // Или сразу готовый результат с download URL
          const downloadUrl = fileData.DOWNLOAD_URL || fileData.downloadUrl || fileData.download_url
            || fileData.DETAIL_URL || fileData.detailUrl || fileData.detail_url || ''
          const fileId = fileData.ID || fileData.FILE_ID || fileData.id || fileData.fileId || ''
          const viewUrl = fileData.VIEW_URL || fileData.viewUrl || ''
          resolve({ downloadUrl: downloadUrl || viewUrl, fileId, name: file.name })
        } catch (e) {
          reject(new Error('Ошибка парсинга ответа Bitrix: ' + e.message))
        }
      } else {
        reject(new Error(`Bitrix upload error: ${xhr.status} ${xhr.responseText}`))
      }
    })

    xhr.addEventListener('error', () => reject(new Error('Network error при загрузке в Bitrix')))
    xhr.addEventListener('timeout', () => reject(new Error('Timeout при загрузке в Bitrix')))
    xhr.send(fd)
  })

  // Двухшаговый upload: если Bitrix вернул uploadUrl — отправляем файл туда
  if (firstResult.needsSecondStep && firstResult.uploadUrl) {
    addLog(`Шаг 2: отправляю файл по uploadUrl...`)
    const fd2 = new FormData()
    fd2.append(firstResult.field || 'file', file, file.name)

    const secondResult = await new Promise((resolve, reject) => {
      const xhr2 = new XMLHttpRequest()
      xhr2.open('POST', firstResult.uploadUrl, true)
      xhr2.upload.addEventListener('progress', (e) => { if (onProgress) onProgress(e) })
      xhr2.addEventListener('load', () => {
        if (xhr2.status >= 200 && xhr2.status < 300) {
          try {
            const res = JSON.parse(xhr2.responseText)
            addLog(`Шаг 2 ответ: ${JSON.stringify(res?.result || res).slice(0, 500)}`)
            const rd = res?.result || {}
            const downloadUrl = rd.DOWNLOAD_URL || rd.downloadUrl || rd.DETAIL_URL || rd.detailUrl || ''
            const fileId = rd.ID || rd.FILE_ID || rd.id || ''
            resolve({ downloadUrl, fileId, name: file.name })
          } catch (e) {
            reject(new Error('Ошибка парсинга шага 2: ' + e.message))
          }
        } else {
          reject(new Error(`Шаг 2 ошибка: ${xhr2.status} ${xhr2.responseText.slice(0, 300)}`))
        }
      })
      xhr2.addEventListener('error', () => reject(new Error('Network error на шаге 2')))
      xhr2.send(fd2)
    })
    return secondResult
  }

  return firstResult
}

// Функция для загрузки файлов с прогресс-баром и диагностикой
const uploadFile = async (file) => {
  diagnosticLog.value = []
  showDiagnostic.value = true
  uploadFailed.value = false

  addLog(`== Начало загрузки ==`)
  addLog(`Файл: ${file.name} | Размер: ${(file.size / 1024).toFixed(1)} KB | Тип: ${file.type}`)

  if (file.size > MAX_FILE_SIZE_BYTES) {
    addLog(`ОШИБКА: файл превышает лимит ${MAX_FILE_SIZE_MB} MB`)
    toast.error(`Файл слишком большой. Максимальный размер: ${MAX_FILE_SIZE_MB} МБ`)
    return null
  }

  isUploading.value = true
  uploadProgress.value = 0
  const startTime = Date.now()

  // Пробуем загрузить через Bitrix Disk (first-party, быстро)
  const bitrixAuth = getBitrixAuth()
  if (bitrixAuth && bitrixAuth.access_token) {
    addLog(`Метод: Bitrix Disk (first-party upload)`)
    try {
      let lastLogPct = -1
      const result = await uploadViaBitrix(file, addLog, (e) => {
        if (e.total) {
          const pct = Math.round((e.loaded * 100) / e.total)
          uploadProgress.value = pct
          const elapsed = ((Date.now() - startTime) / 1000).toFixed(1)
          if (pct >= lastLogPct + 10 || pct === 100) {
            const speed = (e.loaded / ((Date.now() - startTime) / 1000) / 1024).toFixed(1)
            addLog(`[${elapsed}s] ${pct}% | ${(e.loaded / 1024).toFixed(0)}/${(e.total / 1024).toFixed(0)} KB | ${speed} KB/s`)
            lastLogPct = pct
          }
        }
      })

      const elapsed = ((Date.now() - startTime) / 1000).toFixed(1)

      if (result.downloadUrl) {
        addLog(`УСПЕХ (Bitrix) за ${elapsed}s | fileId=${result.fileId}`)
        addLog(`Сохраняю на наш сервер...`)

        // Отправляем Bitrix URL на наш бэкенд — он скачает и сохранит локально
        let jwtToken = ''
        try { jwtToken = localStorage.getItem('jwt_token') || '' } catch (e) { void e }
        const saveRes = await fetch('/api/upload/from-bitrix', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {}),
          },
          body: JSON.stringify({
            downloadUrl: result.downloadUrl,
            name: file.name,
            bitrixFileId: result.fileId,
          }),
        })

        if (saveRes.ok) {
          const saveData = await saveRes.json()
          const totalElapsed = ((Date.now() - startTime) / 1000).toFixed(1)
          addLog(`Сохранено! URL: ${saveData.url} | Всего: ${totalElapsed}s`)
          return saveData.url
        }

        // Если сохранение не удалось — используем Bitrix URL как fallback
        addLog(`Сохранение не удалось (${saveRes.status}), используем Bitrix URL`)
        return result.downloadUrl
      }
      throw new Error('Bitrix не вернул download URL')
    } catch (error) {
      addLog(`Bitrix upload не удался: ${error.message}`)
      addLog(`Пробую fallback через наш сервер...`)
    }
  } else {
    addLog(`BX24 SDK недоступен, загрузка через наш сервер`)
  }

  // Fallback: загрузка через наш бэкенд (медленная из-за iframe throttling)
  addLog(`Метод: наш сервер (fallback)`)
  try {
    const formData = new FormData()
    formData.append('file', file)

    let lastLogPct = -1
    let firstProgress = true
    let lastEventTime = Date.now()
    let lastEventPct = 0
    const uploadStart = Date.now()

    const stallTimer = setInterval(() => {
      const stallSec = ((Date.now() - lastEventTime) / 1000).toFixed(0)
      const totalSec = ((Date.now() - uploadStart) / 1000).toFixed(0)
      if (stallSec >= 5) {
        addLog(`[${totalSec}s] ... нет прогресса ${stallSec}s (застряло на ${lastEventPct}%)`)
      }
    }, 5000)

    let data
    try {
      data = await api.uploadFile(formData, (progressEvent) => {
        const now = Date.now()
        const elapsed = ((now - uploadStart) / 1000).toFixed(1)
        if (firstProgress) { addLog(`[${elapsed}s] Соединение установлено`); firstProgress = false }
        lastEventTime = now
        if (progressEvent.total) {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          uploadProgress.value = pct
          lastEventPct = pct
          if (pct >= lastLogPct + 10 || pct === 100) {
            const speed = (progressEvent.loaded / ((now - uploadStart) / 1000) / 1024).toFixed(1)
            addLog(`[${elapsed}s] ${pct}% | ${(progressEvent.loaded / 1024).toFixed(0)} KB | ${speed} KB/s`)
            lastLogPct = pct
          }
        }
      })
    } finally {
      clearInterval(stallTimer)
    }

    const elapsed = ((Date.now() - startTime) / 1000).toFixed(1)
    addLog(`УСПЕХ (fallback) за ${elapsed}s | URL: ${data.url}`)
    return data.url
  } catch (error) {
    const elapsed = ((Date.now() - startTime) / 1000).toFixed(1)
    addLog(`ОШИБКА через ${elapsed}s: ${error?.message || error}`)
    if (error?.response) {
      addLog(`HTTP: ${error.response.status} | ${JSON.stringify(error.response.data)}`)
    }
    toast.error(error?.response?.data?.message || 'Ошибка загрузки файла')
    console.error('Диагностика загрузки:', diagnosticLog.value.join('\n'))
    uploadFailed.value = true
    return null
  } finally {
    isUploading.value = false
    uploadProgress.value = 0
  }
}

// Функция для вставки файла как блота
const insertFileBlock = (quill, fileName, fileUrl) => {
  const range = quill.getSelection(true)
  quill.insertEmbed(range.index, 'file-block', {
    name: fileName,
    url: fileUrl
  })
  quill.setSelection(range.index + 1)
}

// Регистрация пользовательских обработчиков для загрузки файлов
const registerImageHandler = (quill) => {
  const toolbar = quill.getModule('toolbar')
  toolbar.addHandler('image', () => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')
    input.click()
    
    input.onchange = async () => {
      const file = input.files[0]
      if (file) {
        const url = await uploadFile(file)
        if (url) {
          const range = quill.getSelection(true)
          quill.insertEmbed(range.index, 'image', url)
          quill.setSelection(range.index + 1)
        }
      }
    }
  })
}

// Регистрация обработчика для загрузки видео
const registerVideoHandler = (quill) => {
  const toolbar = quill.getModule('toolbar')
  toolbar.addHandler('video', () => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'video/*')
    input.click()
    
    input.onchange = async () => {
      const file = input.files[0]
      if (file) {
        const url = await uploadFile(file)
        if (url) {
          const range = quill.getSelection(true)
          quill.insertEmbed(range.index, 'video', url)
          quill.setSelection(range.index + 1)
        }
      }
    }
  })
}

// Регистрация обработчика для загрузки любых файлов
const registerFileHandler = (quill) => {
  // Создаем кастомную кнопку для файлов
  const toolbar = quill.getModule('toolbar')
  const fileButton = toolbar.container.querySelector('.ql-file')
  
  if (fileButton) {
    fileButton.addEventListener('click', () => {
      const input = document.createElement('input')
      input.setAttribute('type', 'file')
      input.click()
      
      input.onchange = async () => {
        const file = input.files[0]
        if (file) {
          const url = await uploadFile(file)
          if (url) {
            const fileName = file.name
            insertFileBlock(quill, fileName, url)
          }
        }
      }
    })
  }
}

// Настройки Quill редактора с улучшенным тулбаром
Quill.register('modules/blotRegistration', function(quill, options) {
  for (const name in options) {
    if (Object.prototype.hasOwnProperty.call(options, name)) {
      quill.constructor.register(options[name]);
    }
  }
});

const quillOptions = ref({
  modules: {
    blotRegistration: {
      'file-block': FileBlot,
    },
    toolbar: {
      container: [
        [{'header': [1, 2, 3, 4, 5, 6, false]}],
        [{'size': ['small', false, 'large', 'huge']}],
        ['bold', 'italic', 'underline', 'strike'],
        [{'color': []}, {'background': []}],
        [{'align': []}],
        [{'list': 'ordered'}, {'list': 'bullet'}],
        [{'indent': '-1'}, {'indent': '+1'}],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'].filter(tool => {
          return portalStore.portalData.isViewFiles ? true : tool === 'link';
        }),
        ['clean']
      ],
      handlers: {
        // Обработчики будут добавлены после инициализации
      }
    }
  },
  placeholder: props.placeholder,
  theme: 'snow'
});

// Отслеживание изменений в props
watch(() => props.modelValue, (newValue) => {
  const normalized = normalizeVideoEmbeds(newValue || '')

  if (normalized !== localContent.value) {
    localContent.value = normalized
  }
})

const onUpdate = (value) => {
  const normalized = normalizeVideoEmbeds(value || '')

  emit('update:modelValue', normalized)
  emit('update:content', normalized) // Для обратной совместимости
}

// Регистрируем обработчики после монтирования компонента




onMounted(() => {
  // Небольшая задержка для уверенности, что редактор полностью инициализирован
  setTimeout(() => {
    if (quillEditor.value) {
      const quill = quillEditor.value.getQuill()

      const toolbar = quill.getModule('toolbar')
      const toolbarContainer = toolbar?.container
 
      // Условное добавление функционала загрузки
      if (portalStore.portalData.isViewFiles) {
        // Создаем кнопку для файлов
        const fileButton = document.createElement('button')
        fileButton.className = 'ql-file'
        fileButton.innerHTML = `
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.6398 4.5662L14.4966 0.646508C14.0559 0.229582 13.4791 0 12.8724 0H4.98438C3.68126 0 2.62109 1.06017 2.62109 2.36328V19.6367C2.62109 20.9398 3.68126 22 4.98438 22H17.0156C18.3187 22 19.3789 20.9398 19.3789 19.6367V6.28298C19.3789 5.63634 19.1095 5.01059 18.6398 4.5662ZM17.3878 5.15625H14.1797C14.0612 5.15625 13.9648 5.05987 13.9648 4.94141V1.91795L17.3878 5.15625ZM17.0156 20.7109H4.98438C4.39205 20.7109 3.91016 20.229 3.91016 19.6367V2.36328C3.91016 1.77096 4.39205 1.28906 4.98438 1.28906H12.6758V4.94141C12.6758 5.77066 13.3504 6.44531 14.1797 6.44531H18.0898V19.6367C18.0898 20.229 17.6079 20.7109 17.0156 20.7109Z" fill="black"/>
          <path d="M15.5977 8.59375H6.14453C5.78858 8.59375 5.5 8.88233 5.5 9.23828C5.5 9.59423 5.78858 9.88281 6.14453 9.88281H15.5977C15.9536 9.88281 16.2422 9.59423 16.2422 9.23828C16.2422 8.88233 15.9536 8.59375 15.5977 8.59375ZM15.5977 12.0312H6.14453C5.78858 12.0312 5.5 12.3198 5.5 12.6758C5.5 13.0317 5.78858 13.3203 6.14453 13.3203H15.5977C15.9536 13.3203 16.2422 13.0317 16.2422 12.6758C16.2422 12.3198 15.9536 12.0312 15.5977 12.0312ZM9.26922 15.4688H6.14453C5.78858 15.4688 5.5 15.7573 5.5 16.1133C5.5 16.4692 5.78858 16.7578 6.14453 16.7578H9.26922C9.62517 16.7578 9.91375 16.4692 9.91375 16.1133C9.91375 15.7573 9.62517 15.4688 9.26922 15.4688Z" fill="black"/>
        </svg>
        `
        fileButton.title = 'Загрузить файл'

        // Добавляем кнопку в последнюю группу перед clean
        const cleanButton = toolbarContainer.querySelector('.ql-clean')
        if (cleanButton && cleanButton.parentNode) {
          cleanButton.parentNode.insertBefore(fileButton, cleanButton)
        }

         // Регистрируем обработчики
         registerImageHandler(quill)
         registerVideoHandler(quill)
         registerFileHandler(quill)
       }

       applyToolbarTooltips(toolbarContainer)
     }
   }, 100)
 })
</script>

<style lang="scss">
.ql-editor-ave {
  position: relative;
}

/* Diagnostic Panel - fixed at bottom of screen */
.diagnostic-panel {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #1a1a2e;
  border-top: 2px solid #3b82f6;
  z-index: 10000;
  max-height: 300px;
  display: flex;
  flex-direction: column;
}

.diagnostic-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #16213e;
}

.diagnostic-title {
  color: #e2e8f0;
  font-weight: 600;
  font-size: 13px;
}

.diagnostic-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.diagnostic-copy-btn-sm {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}

.diagnostic-copy-btn-sm:hover {
  background: #2563eb;
}

.diagnostic-close {
  background: none;
  border: none;
  color: #888;
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.diagnostic-close:hover {
  color: #fff;
}

.diagnostic-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid #333;
}

.upload-progress-container {
  flex: 1;
  height: 6px;
  background: #374151;
  border-radius: 3px;
  overflow: hidden;
}

.upload-progress-bar {
  height: 100%;
  background: #3b82f6;
  border-radius: 3px;
  transition: width 0.2s ease;
}

.upload-pct {
  color: #93c5fd;
  font-size: 12px;
  font-weight: 600;
  min-width: 36px;
  text-align: right;
}

.diagnostic-log {
  color: #a5b4c4;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  line-height: 1.5;
  padding: 8px 12px;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.ql-editor-ave {
  /* Красивые стили для тулбара */
  :deep(.ql-toolbar) {
    border: 1px solid #e5e7eb;
    border-radius: 12px 12px 0 0;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    overflow: visible;
    padding: 12px 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  :deep(.ql-container) {
    border: 1px solid #e5e7eb;
    border-top: none;
    border-radius: 0 0 12px 12px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  /* Стили для кнопок тулбара */
  :deep(.ql-toolbar .ql-formats) {
    margin-right: 12px;
  }

  :deep(.ql-toolbar button) {
    border-radius: 6px;
    padding: 6px;
    margin: 0 2px;
    transition: all 0.2s ease;
    border: 1px solid transparent;
  }

  :deep(.ql-toolbar button:hover) {
    background: rgba(59, 130, 246, 0.1);
    border-color: rgba(59, 130, 246, 0.2);
    transform: translateY(-1px);
  }

  :deep(.ql-toolbar button.ql-active) {
    background: #3b82f6;
    color: white;
    border-color: #2563eb;
  }

  :deep(.ql-toolbar .ql-picker) {
    border-radius: 6px;
    transition: all 0.2s ease;
  }

  :deep(.ql-toolbar .ql-picker:hover) {
    background: rgba(59, 130, 246, 0.1);
  }

  /* Tooltip стили вынесены в глобальный блок <style> без scoped */

  /* Стили для кнопки файла (SVG иконка добавляется программно) */
  :deep(.ql-file) {
    position: relative;
  }

  :deep(.ql-file) svg {
    width: 18px;
    height: 18px;
  }

  /* Стили для редактора */
  :deep(.ql-editor) {
    padding: 20px;
    line-height: 1.6;
    font-size: 14px;
    min-height: 200px;
  }

  :deep(.ql-editor.ql-blank::before) {
    color: #9ca3af;
    font-style: italic;
  }
}

/* Стили для видео */
.ql-video {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  border: none;
  border-radius: 8px;
}

/* Стили для блоков файлов */
.ql-file-block {
  width: 100%;
  white-space: normal;
}

.ql-html-video-wrapper {
  display: block;
  width: 100%;
}

.ql-html-video {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
}

.file-block {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 16px;
  margin: 8px 0;
  transition: all 0.2s ease;
  cursor: default;
}

.file-block:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.file-icon {
  display: flex;
  width: 20px;
  height: 20px;
  margin-right: 12px;
  color: #64748b;
}

.file-info {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.file-name {
  font-weight: 500;
  color: #334155;
  flex: 1;
  margin-right: 12px;
  word-break: break-all;
}

.file-download-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.file-download-btn:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

/* Стили для таблиц */
:deep(.ql-table-block),
:deep(.ql-table) {
  border-collapse: collapse;
  width: 100%;
  margin: 16px 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

:deep(.ql-table-block .ql-table) {
  margin: 0;
  border: none;
}

:deep(.ql-table td) {
  border: 1px solid #e2e8f0;
  padding: 12px;
  min-width: 100px;
  background: white;
  transition: background-color 0.2s ease;
}

:deep(.ql-table td:hover) {
  background: #f8fafc;
}

:deep(.ql-table tr:first-child td) {
  background: #f1f5f9;
  font-weight: 600;
  color: #374151;
}
</style>

<!-- Глобальные стили для tooltip'ов (scoped не работает с динамическими классами) -->
<style>
.ql-tooltip-target {
  position: relative !important;
}

.ql-tooltip-target::before {
  content: attr(data-tooltip);
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) scale(0.9);
  padding: 6px 10px;
  background: #1f2937;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: 0;
  visibility: hidden;
  transition: all 0.15s ease;
  z-index: 99999;
  pointer-events: none;
}

.ql-tooltip-target::after {
  content: '';
  position: absolute;
  top: calc(100% + 2px);
  left: 50%;
  transform: translateX(-50%) scale(0.9);
  border: 5px solid transparent;
  border-bottom-color: #1f2937;
  opacity: 0;
  visibility: hidden;
  transition: all 0.15s ease;
  z-index: 99999;
  pointer-events: none;
}

.ql-tooltip-target:hover::before,
.ql-tooltip-target:hover::after {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) scale(1);
}

</style>
